import { Fact, FactArg, TestAssertion, TestBlock } from "@/src/modeler/polar";
import dynamic from "next/dynamic";
import { TestStatus } from "./ConcreteInteractiveExample";
import { isEqual } from "lodash";

const Graphviz = dynamic(() => import("graphviz-react"), { ssr: false });

function factArgToDot(factArg: FactArg): string {
  if (typeof factArg === "object") {
    return `"${factArg.tag} ${factArg.id}"`;
  } else {
    return `"${factArg}"`;
  }
}

function factToDot(fact: Fact, highlighted: boolean): string {
  switch (fact.name) {
    case "has_role": {
      const [actor, role, resource] = fact.args;
      if (typeof role !== "string") {
        throw new Error("Invalid fact");
      }
      return `${factArgToDot(actor)} -- ${factArgToDot(
        resource
      )} [label="${role} role"${
        highlighted ? ", penwidth=4" : ""
      }]\n${factArgToDot(actor)} [shape=oval]`;
    }
    case "has_relation": {
      const [child, relation, parent] = fact.args;
      if (typeof relation !== "string") {
        throw new Error("Invalid fact");
      }
      return `${factArgToDot(child)} -- ${factArgToDot(
        parent
      )} [label="${relation} relation"${highlighted ? ", penwidth=4" : ""}]`;
    }
    default: {
      return "";
    }
  }
}

function assertionToDot(
  assertion: TestAssertion,
  testResult: boolean | null,
  highlighted: boolean
): string {
  const [actor, action, resource] = assertion.fact.args;
  if (assertion.fact.name !== "allow" || typeof action !== "string") {
    throw new Error("Invalid assertion");
  }
  return `${factArgToDot(actor)} -- ${factArgToDot(
    resource
  )} [label="${action} permission", style=dashed, color=${
    testResult === true ? "green" : testResult === false ? "red" : "gray"
  }${highlighted ? ", penwidth=4" : ""}]\n${factArgToDot(actor)} [shape=oval]`;
}

function testToDot(test: TestBlock, testStatus: TestStatus): string {
  const stmts = [
    "edge [penwidth=2]",
    "node [shape=box]",
    ...test.setup.map((fact) =>
      factToDot(fact, isEqual(fact, testStatus.highlightedFact))
    ),
    ...test.assertions.map((assertion, i) =>
      assertionToDot(
        assertion,
        testStatus.results[i],
        isEqual(assertion.fact, testStatus.highlightedFact)
      )
    ),
  ];
  if (testStatus.highlightedObject) {
    stmts.push(`${factArgToDot(testStatus.highlightedObject)} [penwidth=4]`);
  }
  return `graph {\n${stmts.join("\n")}\n}`;
}

const TestVisualization: React.FC<{
  test: TestBlock;
  testStatus: TestStatus;
  width: number;
  height: number;
}> = ({ test, testStatus, width, height }) => (
  <Graphviz dot={testToDot(test, testStatus)} options={{ width, height }} />
);

export default TestVisualization;
